/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ICipBackTopFooterViewProps } from './cip-back-top-footer';
import { BACK_TO_TOP_HIDE_DELAY, BACK_TO_TOP_THRESHOLD } from '../../configuration';

export default (props: ICipBackTopFooterViewProps) => {
    // Sets the visibility of the back to top button.
    const [visible, setVisible] = useState<boolean>(false);
    // Sets the scrolling state of the page.
    const [isScrolling, setIsScrolling] = useState<boolean>(false);
    /**
     * Ref to the scroll value.
     * Due to the nature of the scroll event, we need to keep track of the previous scroll value.
     * If we dont do this, the event will call the setVisible function multiple times,
     * even if the user is not scrolling.
     */
    const scrollValue = useRef(0);
    /**
     * Sets the mouse over state of the button.
     * This is used to check if the user is hovering over the button
     * to prevent the button from hiding.
     */
    const [isMouseOver, setIsMouseOver] = useState(false);
    /**
     * Ref to the back to top button.
     * This is used to add mouse event listeners to the button,
     * to check if the user is hovering over the button.
     */
    const btnRef = useRef<HTMLAnchorElement>(null);

    const scrollToTop = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
        event.preventDefault();
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    const handleScroll = (): void => {
        setIsScrolling(true);
        const scrolled: number = document.documentElement.scrollTop;
        if (scrollValue.current !== scrolled) {
            if (scrolled > BACK_TO_TOP_THRESHOLD) {
                setVisible(true);
            }
            setIsScrolling(false);

            if (scrolled <= BACK_TO_TOP_THRESHOLD) {
                setVisible(false);
            }
            scrollValue.current = scrolled;
        }
    };

    /**
     * This effect is used to check if the user is scrolling the page.
     * If so, we check if the user has scrolled past the threshold to show the back to top button.
     */
    useEffect(() => {
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    /**
     * This effect is used to check if the user is hovering over the back to top button.
     * If the user is hovering over the button, we prevent the button from hiding.
     */
    useEffect(() => {
        const button = btnRef.current;

        const handleMouseEnter = () => {
            setIsMouseOver(true);
        };

        const handleMouseLeave = () => {
            setIsMouseOver(false);
        };

        if (button) {
            button.addEventListener('mouseenter', handleMouseEnter);
            button.addEventListener('mouseleave', handleMouseLeave);
        }

        return () => {
            if (button) {
                button.removeEventListener('mouseenter', handleMouseEnter);
                button.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []);

    /**
     * This effect is used to hide the back to top button if the user is not scrolling
     * and not hovering over the button.
     */
    useEffect(() => {
        let hideTimeout: NodeJS.Timeout;
        if (!isScrolling && !isMouseOver) {
            hideTimeout = setTimeout(() => {
                setVisible(false);
            }, BACK_TO_TOP_HIDE_DELAY);
        }
        return () => clearTimeout(hideTimeout);
    }, [isScrolling, isMouseOver]);

    return (
        <a
            className={`cip-back-top-footer ${visible && 'is-visible'}`}
            onClick={scrollToTop}
            role='link'
            ref={btnRef}
            href={props.config.destination}
            title={props.config.title}
            aria-label={props.config.ariaLabel}
            aria-hidden={!visible}
            tabIndex={visible ? 0 : -1}
        >
            <span className='sr-only'>{props.config.backtoTopText}</span>
        </a>
    );
};
